// 混合拆单与海快家具规则相关 getters（纯派生，不产生副作用）
import {
  SPLIT_MODES,
  buildSplitCopy,
  buildSplitParcelNote,
} from '@g/order-create/split/mixedSplit'
import { estimateSecondOrder, formatEstimateRows } from '@g/order-create/split/secondOrderEstimate'
import {
  buildSeFurnitureCopy,
  computeSeFurnitureRule,
  isSeFurnitureAckCovering,
  SE_FURNITURE_BOTTOM_LOCK,
  SE_FURNITURE_LEVELS,
} from '@g/order-create/split/seFurnitureRule'
import { buildSeaForcedCopy, computeSeaForcedRule } from '@g/order-create/split/seaForcedRule'

function pickParcels (model, pclids = []) {
  const parcels = model?.parcels || []
  return pclids.map(pclid => parcels.find(parcel => String(parcel.pclid) === String(pclid))).filter(Boolean)
}

export const getters = {
  createSplit (state) {
    return state.createView?.split || null
  },
  splitActive (state, getters) {
    return Boolean(getters.createSplit?.active)
  },
  splitSeaMode (state, getters) {
    return getters.splitActive && getters.createSplit.mode === SPLIT_MODES.SEA
  },
  // 海運模式只代表主模型是家具子集；混合勾选进入页面时不替客人选线路，例外件处理与下一步锁定要等客人真的选了海運。
  splitSeaSelected (state, getters) {
    return getters.splitSeaMode && getters.shippingRatesInfo?.transport === 'sea'
  },
  splitOtherParcels (state, getters) {
    const split = getters.createSplit
    return split?.active ? pickParcels(split.allModel, split.otherPclids) : []
  },
  // 全部已勾选的包裹（含例外件），供摘要行「已勾選 N 件」使用。
  splitAllParcels (state, getters) {
    const split = getters.createSplit
    return split?.active ? (split.allModel?.parcels || []) : []
  },
  // 例外件卡片逐件标注（pclid → { text, resolved }）。
  splitOtherParcelNotes (state, getters) {
    const split = getters.createSplit
    const notes = {}
    getters.splitOtherParcels.forEach(parcel => {
      notes[parcel.pclid] = buildSplitParcelNote({
        parcel,
        decision: split?.decision || '',
        keepOnlyPclids: split?.keepOnlyPclids || [],
      })
    })
    return notes
  },
  splitSecondParcels (state, getters) {
    const split = getters.createSplit
    return split?.active ? pickParcels(split.allModel, split.secondPclids) : []
  },
  splitKeepOnlyParcels (state, getters) {
    const split = getters.createSplit
    return split?.active ? pickParcels(split.allModel, split.keepOnlyPclids) : []
  },
  // 第二单预估：把例外件模型交给临时 store 跑同一套 feeSummary（税制按页面恢复规则、地址沿用第一单）。
  secondOrderEstimate (state, getters, rootState, rootGetters) {
    if (!getters.splitActive || !getters.createSplit.otherModel) return null
    return estimateSecondOrder({
      model: getters.createSplit.otherModel,
      addrInfo: state.currAddrInfo,
      tradevanAddr: state.currTradevanAddr,
      rootState,
      rootGetters,
      mainUseFeeType: state.createForm?.useFeeType || '',
    })
  },
  // 拆单海運模式下本单（家具）的海運运费，供状态条与对话框文案使用。
  splitSeaFee (state, getters) {
    if (!getters.splitSeaMode || !state.createPrepare?.parcels) return 0
    return getters.seaParcelsFeeInfo?.parcelsFee || 0
  },
  splitCopy (state, getters) {
    const split = getters.createSplit
    return buildSplitCopy({
      others: getters.splitOtherParcels,
      secondable: getters.splitSecondParcels,
      keepOnly: getters.splitKeepOnlyParcels,
      seaCount: state.createPrepare?.parcels?.length || 0,
      seaFee: getters.splitSeaFee,
      estimateTotal: getters.secondOrderEstimate?.total ?? null,
      estimateRowsText: formatEstimateRows(getters.secondOrderEstimate?.rows || [], getters.secondOrderEstimate?.taxNote),
      decision: split?.decision || '',
    })
  },
  // 当前主模型（即本单）的海快家具规则。
  seFurnitureRule (state) {
    const prepare = state.createPrepare
    return computeSeFurnitureRule({
      parcels: prepare?.parcels || [],
      jyAddrid: prepare?.parcelsInfo?.jyAddrid,
      provider: prepare?.lcmrouteSe?.twcc?.provider,
    })
  },
  // 点海快卡时适用的规则：拆单海運模式下海快会带上全部包裹，所以按全部包裹计算。
  seFurnitureRuleForSe (state, getters) {
    const split = getters.createSplit
    if (getters.splitSeaMode && split.allModel) {
      return computeSeFurnitureRule({
        parcels: split.allModel.parcels || [],
        jyAddrid: split.allModel.parcelsInfo?.jyAddrid,
        provider: split.allModel.lcmrouteSe?.twcc?.provider,
      })
    }
    return getters.seFurnitureRule
  },
  seFurnitureAckValid (state, getters) {
    return isSeFurnitureAckCovering(state.createView?.seFurnitureAckKey || '', getters.seFurnitureRule)
  },
  seFurnitureSelectedSe (state, getters) {
    return getters.shippingRatesInfo?.transport === 'se'
  },
  seFurnitureLimitLocked (state, getters) {
    return getters.seFurnitureSelectedSe && getters.seFurnitureRule.level === SE_FURNITURE_LEVELS.LIMIT
  },
  seFurnitureBlocking (state, getters) {
    return getters.seFurnitureSelectedSe &&
      getters.seFurnitureRule.level !== SE_FURNITURE_LEVELS.NONE &&
      !getters.seFurnitureAckValid
  },
  seFurnitureCopy (state, getters) {
    return buildSeFurnitureCopy(getters.seFurnitureRule, { confirmed: getters.seFurnitureAckValid })
  },
  seFurnitureDeclRedText (state, getters) {
    return getters.seFurnitureLimitLocked ? getters.seFurnitureCopy.declRedText : ''
  },
  // 家具棧板費強制走海運：拆单看全部包裹里的家具，纯家具勾选看主模型；
  // 家具子集 prepare 失败退回整單海快时不锁，避免客人无路可走。
  seaForcedRule (state, getters, rootState) {
    const split = getters.createSplit
    const model = getters.splitActive ? split.allModel : state.createPrepare
    return computeSeaForcedRule({
      parcels: model?.parcels || [],
      jyAddrid: model?.parcelsInfo?.jyAddrid,
      seaAddrids: rootState?.parcel?.seaAddrids || [],
      enabled: !(getters.splitActive && split.mode === SPLIT_MODES.SE),
    })
  },
  seaForcedCopy (state, getters) {
    return buildSeaForcedCopy(getters.seaForcedRule)
  },
  // 强制走海運却仍选中海快（例如载入时恢复了上次线路）时锁「下一步」。
  seaForcedSeSelected (state, getters) {
    return getters.seaForcedRule.forced && getters.shippingRatesInfo?.transport === 'se'
  },
  // 第一步「下一步」被锁定的原因；空字符串表示可继续。
  splitBlockReason (state, getters) {
    if (getters.splitActive && !getters.shippingRatesInfo?.transport) return getters.splitCopy.bottomSelectLock
    if (getters.seaForcedSeSelected) return getters.seaForcedCopy.bottomLock
    if (getters.splitSeaSelected && !getters.createSplit.decision) return getters.splitCopy.bottomLock
    if (getters.seFurnitureBlocking) return SE_FURNITURE_BOTTOM_LOCK
    return ''
  },
}
